import type { VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@homeinstead/ui";
import { cva } from "class-variance-authority";
import NextLink from "next/link";

const linkVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg font-jakarta text-base ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    defaultVariants: {
      size: "primary",
      variant: "primary",
    },
    variants: {
      size: {
        icon: "h-10 w-10",
        inline: "h-fit p-0",
        lg: "h-11 rounded-full px-8",
        primary: "h-12 px-4 py-2",
        sm: "h-9 rounded-md px-3",
      },
      variant: {
        friendly: `bg-friendly-base
        hover:bg-friendly-tints-800 hover:outline hover:outline-2 hover:outline-friendly-base
        focus:bg-friendly-tints-800 focus:border-none`,
        "outline-dark": `bg-new-beginnings-base text-white border-2 border-white
        hover:text-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:border-2 hover:border-white
        focus:text-new-beginnings-base focus:bg-white focus:border-2 focus:border-new-beginnings-base focus:outline focus:outline-2 focus:outline-white`,
        plain: `justify-start underline hover:no-underline`,
        primary: `bg-new-beginnings-base text-white 
          hover:bg-new-beginnings-tints-800 hover:text-new-beginnings-base hover:shadow-primary-button
          hover:ring hover:ring-2 hover:ring-new-beginnings-base
          focus:outline focus:outline-2 focus:border-2 focus:border-white focus:outline-new-beginnings-base`,
        secondary: `border-2 border-new-beginnings-base bg-background text-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:shadow-primary-button focus:border-2 focus:border-white focus:outline focus:outline-2 focus:outline-new-beginnings-base 
                    hidden h-12 py-2 font-semibold md:inline-flex`,
      },
    },
  },
);

export interface LinkProps
  extends React.ComponentPropsWithRef<typeof NextLink>,
    VariantProps<typeof linkVariants> {
  className?: string;
  children?: React.ReactNode;
}

const Link: React.FC<LinkProps> = ({
  className,
  variant,
  size,
  children,
  ...props
}) => {
  return (
    <NextLink
      className={cn(linkVariants({ className, size, variant }))}
      {...props}
    >
      {children}
    </NextLink>
  );
};

Link.displayName = "Link";

export { Link, linkVariants };
